import React from "react";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MEETING_LINKS, HUBSPOT_MEETING_BASE } from "@/lib/meetingLinks";

/**
 * BookConsultationButton - Reusable button for booking HubSpot meetings
 * 
 * @param {string} meetingType - Type of meeting (general, seo, aeo, geo, gym, hvac, etc.)
 * @param {string} variant - Button variant (default, outline, link)
 * @param {string} size - Button size (default, sm, lg)
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Custom button text
 * @param {boolean} showIcon - Show calendar icon (default: true)
 * @param {boolean} openInNewTab - Open in new tab (default: true)
 */
const BookConsultationButton = ({ 
  meetingType = 'general',
  variant = 'default',
  size = 'default',
  className = '',
  children,
  showIcon = true,
  openInNewTab = true
}) => {
  const meetingUrl = MEETING_LINKS[meetingType] || HUBSPOT_MEETING_BASE;
  
  const defaultText = {
    general: 'Book Free Consultation',
    seo: 'Book SEO Strategy Call',
    aeo: 'Book AEO Strategy Call',
    geo: 'Book GEO Strategy Call',
    gym: 'Book Gym Marketing Call',
    hvac: 'Book HVAC Marketing Call',
    landscaping: 'Book Landscaping Call',
    lawFirm: 'Book Law Firm Call',
    healthcare: 'Book Healthcare Call',
    realEstate: 'Book Real Estate Call',
    aiReceptionist: 'Book AI Demo Call',
    videoMarketing: 'Book Video Strategy Call',
    webDesign: 'Book Web Design Call',
    ppc: 'Book PPC Strategy Call',
  };

  const buttonText = children || defaultText[meetingType] || 'Book Free Consultation';

  const buttonClasses = variant === 'default' 
    ? `bg-[#00FF85] hover:bg-[#00FF85]/90 text-black font-semibold ${className}`
    : variant === 'outline'
    ? `border-white/20 text-white hover:bg-white/10 ${className}`
    : className;

  return (
    <Button 
      asChild
      size={size}
      variant={variant === 'outline' ? 'outline' : variant === 'link' ? 'link' : 'default'}
      className={buttonClasses}
    >
      <a 
        href={meetingUrl} 
        target={openInNewTab ? "_blank" : "_self"}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        data-testid={`book-consultation-${meetingType}`}
      >
        {showIcon && <Calendar className="w-5 h-5 mr-2" />}
        {buttonText}
      </a>
    </Button>
  );
};

export default BookConsultationButton;
